"use client";

import { createContext, useContext, useMemo } from "react";
import {
  createPluralTranslator,
  createTranslator,
  type PluralTranslator,
  type TranslationEntries,
  type Translator,
} from "@tripcart/shared/i18n/t";

export type { Translator, PluralTranslator };

const TranslationContext = createContext<TranslationEntries>({});

/**
 * Seeds the agency's saved translation overrides for client components. Entries
 * are resolved server-side (root layout) and passed as a plain prop — no client
 * fetch. English-default agencies pass `{}`, so the payload is empty and every
 * `t("English")` falls back to its own literal key. Mirrors the server-side
 * `getTranslator`, so a string reads identically whether rendered on the server
 * or in a client island.
 */
export function TranslationProvider({
  entries,
  children,
}: {
  entries: TranslationEntries;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <TranslationContext.Provider value={entries}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Client-side `t(key, vars?)`. The English source string IS the key, so an
 * untranslated phrase renders its own literal. Bound once per entries identity.
 */
export function useTranslator(): Translator {
  const entries = useContext(TranslationContext);
  return useMemo(() => createTranslator(entries), [entries]);
}

/**
 * Client-side `tn(single, plural, count, vars?)` — plural counterpart of
 * `useTranslator`, modelled on WordPress `_n`. Singular when count === 1.
 */
export function usePluralTranslator(): PluralTranslator {
  const entries = useContext(TranslationContext);
  return useMemo(() => createPluralTranslator(entries), [entries]);
}
